import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { PortalOrdersBoard } from '@api/BoardSDK';
import { executeIntegration } from '@api/integration-block.server';
import { withBoardAuth } from '@generated/server/boardAuth.server';
import { requireStaff } from '@generated/server/staff.server';

const GMAIL = 'c70cb228-a141-49d4-a586-364172111780';
const GEORGE_EMAIL = 'gbrook8344@gmail.com';
const GEORGE_ID = '106410322';
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const COLS = ['status', 'orderDate', 'totalContractValue', 'prepaymentDiscount', 'paymentMethod', 'adFiles', 'specialInstructions', 'contactPerson', 'email', 'phone', 'publication', 'adSize', 'duration', 'startMonth', 'monthlyRate'] as const;

const usd = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const ascii = (s: string) => s.normalize('NFKD').replace(/\u2026/g, '...').replace(/[\u2013\u2014]/g, '-').replace(/\u00b7/g, '-').replace(/[^\t\n\r\x20-\x7e]/g, '');

function monthList(start: string, count: number): string[] {
  const [m, y] = start.trim().split(/\s+/);
  const idx = MONTHS.indexOf(m ?? '');
  const year = Number(y);
  if (idx < 0 || !year) return [start];
  return Array.from({ length: count }, (_, i) => `${MONTHS[(idx + i) % 12]} ${year + Math.floor((idx + i) / 12)}`);
}

type Order = { id: string; name: string } & Partial<Record<(typeof COLS)[number], unknown>>;

function buildContract(o: Order) {
  const months = Math.max(1, parseInt(String(o.duration ?? '1'), 10) || 1);
  const issues = monthList(String(o.startMonth ?? ''), months);
  const total = Number(o.totalContractValue ?? 0);
  const monthly = Number(o.monthlyRate ?? 0);
  const paid = o.status === 'Paid';
  const files = Array.isArray(o.adFiles) ? o.adFiles.length : 0;
  const notes = String(o.specialInstructions ?? '');
  const wantsHelp = /ad helper|design help|sketch/i.test(notes);
  const copy = files > 0
    ? `Received (${files} file${files === 1 ? '' : 's'})${wantsHelp ? ' - we will design your ad and email you a proof' : ''}`
    : wantsHelp ? 'Design requested - we will email you a proof' : 'Not yet received - please email your ad to ' + GEORGE_EMAIL;
  const payment = paid
    ? `PAID - ${usd(o.prepaymentDiscount ? total : monthly || total)} received${o.prepaymentDiscount ? ' (paid in full)' : ''}`
    : `NOT YET PAID - ${o.prepaymentDiscount ? `${usd(total)} due before the first issue` : `${usd(monthly)} due each month`}`;
  const email = (o.email as { email?: string } | null)?.email ?? '';
  const ref = `SR-${o.id.slice(-6)}`;

  const body = ascii([
    `STAR-REVUE ADVERTISING CONTRACT  (Order ${ref})`,
    '',
    `Advertiser: ${o.name}`,
    `Contact: ${o.contactPerson ?? ''}${email ? ` - ${email}` : ''}`,
    '',
    `Publication: ${o.publication ?? ''}`,
    `Ad size: ${o.adSize ?? ''}`,
    `Duration: ${o.duration ?? `${months} month(s)`}`,
    `Issues: ${issues.join(', ')}`,
    '',
    `Monthly rate: ${usd(monthly)}`,
    `Total contract value: ${usd(total)}${o.prepaymentDiscount ? ' (prepayment discount applied)' : ''}`,
    `Payment method: ${o.paymentMethod ?? ''}`,
    `Payment status: ${payment}`,
    '',
    `Ad copy: ${copy}`,
    '',
    'Thank you for advertising with the Star-Revue. Reply to this email or call us with any changes.',
    'George Fiala, Publisher',
  ].join('\n'));
  return { to: email, subject: ascii(`Your Star-Revue advertising contract - ${o.name} (${ref})`), body };
}

async function send(to: string, subject: string, body: string) {
  return withBoardAuth(() => executeIntegration(GMAIL, { to: [to], subject, body }));
}

/** Emails the contract to the advertiser and George. Public (after checkout) or staff (resend). */
export const sendContract = createServerFn({ method: 'POST' })
  .validator(z.object({ orderId: z.string().regex(/^\d+$/), pass: z.string().optional(), staff: z.boolean().optional() }))
  .handler(async ({ data }) => {
    if (data.staff) await requireStaff(data.pass);
    const o = await withBoardAuth(() => new PortalOrdersBoard().item(data.orderId).withColumns([...COLS]).execute());
    if (!o) throw new Error('Order not found');
    const c = buildContract(o as unknown as Order);
    const result = { customer: false, george: false, notified: false, why: '' as string };
    if (c.to) {
      try { await send(c.to, c.subject, c.body); result.customer = true; }
      catch (e) { console.error('[contract] customer email failed', e); result.why = (e as { code?: string })?.code ?? 'failed'; }
    }
    try { await send(GEORGE_EMAIL, `Copy: ${c.subject}`, `${result.customer ? 'Sent to' : 'COULD NOT be emailed to'} ${c.to || 'the advertiser (no email)'}.\n\n${c.body}`); result.george = true; }
    catch (e) { console.error('[contract] george email failed', e); }
    if (!result.george || !result.customer) {
      try {
        await withBoardAuth(() => new PortalOrdersBoard().item(data.orderId).notify(GEORGE_ID)
          .create(`Contract for ${o.name}: ${result.customer ? 'emailed to advertiser' : 'NOT emailed to advertiser - open the order and press Send contract'}.`).execute());
        result.notified = true;
      } catch (e) { console.error('[contract] notification failed', e); }
    }
    return result;
  });
